import type { WebediaPostrollConfig } from '@webedia-services/webedia-player';
import type { WebediaJWPOptions } from '@webedia-services/webedia-player/jwplayer';

import {
  getClientConfiguration,
  getPrerollConfiguration
} from 'common/module/advertisement';
import { addClass } from 'common/tools/dom/classes';
import { getImageUrl } from 'common/tools/network/url';

import type { PlayerCustomData } from 'website/module/player';
import type { PlayerCustomDataPartner } from 'website/module/player';

/**
 * function is used to override config of player by partner player's config
 */
export const overridePartnerOptions = (
  config: WebediaJWPOptions,
  {
    preRoll,
    postRoll,
    endScreen,
    soundOnOver,
    logoSource,
    logoTarget,
    postrollPreviewOverlayMessage,
    postrollOverlayDisplay,
    hideTopRightMenu
  }: PlayerCustomDataPartner,
  postrollConfig: WebediaPostrollConfig,
  playerContainer?: HTMLElement
) => {
  const partnerConfig = { ...config };

  if (!partnerConfig.jwpOptions) {
    partnerConfig.jwpOptions = {};
  }

  if (logoSource) {
    partnerConfig.jwpOptions.logo = {
      file: getImageUrl({
        src: logoSource,
        protocol: 'http'
      }),
      link: logoTarget ?? undefined,
      hide: false,
      // is not possible to change the position of the logo bug fixed in 7.3, only top-left applied
      // @see also https://support.jwplayer.com/customer/en/portal/articles/1403726-jw-player-7-release-notes
      // position: 'bottom-right'
      position: 'top-right'
    };
  }
  if (!partnerConfig.advertising) {
    // If advertising options are defined, the client is mandatory.
    // Possible values include:
    //
    // • dai: Use the Google IMA SDK for dynamic ad insertion
    //
    // • freewheel: Use the Freewheel client
    //
    // • googima: Use the Google IMA SDK - Required for certain ad tags
    //
    // • vast: Use the JW Player VAST client
    //
    // https://developer.jwplayer.com/jwplayer/docs/jw8-player-configuration-reference#advertising
    partnerConfig.advertising = { client: 'vast' };
  }
  partnerConfig.advertising.disablePreroll = preRoll === false;
  const partnerPostrollAsVideo =
    postRoll === false ? undefined : postrollConfig;
  partnerConfig.jwpOptions.related =
    endScreen === false ? undefined : partnerConfig.jwpOptions.related;
  partnerConfig.soundOnOver = !!soundOnOver;
  partnerConfig.jwpOptions.mute = partnerConfig.soundOnOver;
  if (partnerPostrollAsVideo) {
    partnerPostrollAsVideo.previewMessage =
      postrollPreviewOverlayMessage || partnerPostrollAsVideo.previewMessage;
    partnerPostrollAsVideo.overlayActive =
      typeof postrollOverlayDisplay === 'undefined'
        ? true
        : postrollOverlayDisplay;
  }

  if (hideTopRightMenu && playerContainer) {
    addClass(playerContainer, 'hide-top-right-menu');
  }

  return { partnerConfig, partnerPostrollAsVideo };
};

// see https://developer.jwplayer.com/jw-player/demos/advanced/minimize-and-float-video-on-scroll/
export const getAdvertisingConfiguration = async (data: PlayerCustomData) => {
  const prerollSetting = await getPrerollConfiguration(data);
  const advertisingSetting = getClientConfiguration();

  return {
    ...data,
    ...advertisingSetting,
    ...(prerollSetting instanceof Object ? prerollSetting : {})
  };
};
